<template>
  <manual-page
    title="Citas con Especialista"
    description="Revisa las pre-solicitudes de cita de los clientes y respóndelas: comparte la agenda, rechaza con justificación o pide más detalles."
    :show-video="false"
    :related-links="relatedLinks"
    :action-route="{ name: 'appointmentRequestsIndex', query: { status: 'pending' } }"
    action-text="Ver citas pendientes"
  >
    <!-- Overview -->
    <h2>¿Cómo funciona?</h2>
    <p>
      Los clientes pueden solicitar una cita con un especialista desde su portal
      (sección "Cita con especialista"), exponiendo el motivo de su consulta y,
      opcionalmente, la mascota. Cada solicitud llega como una
      <strong>pre-solicitud</strong> a la sección
      <strong>Citas con especialista</strong> del panel, donde el equipo la
      revisa antes de liberar la agenda.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Reglas del flujo:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Cada cliente solo puede tener una solicitud abierta a la vez.</li>
        <li>Recibes una notificación por cada solicitud nueva o respondida.</li>
        <li>
          Toda respuesta (aprobación, rechazo o pregunta) le llega al cliente por
          notificación en la app y por correo.
        </li>
      </ul>
    </div>

    <!-- Triage -->
    <h2>Atender una solicitud</h2>
    <p>
      Abre la solicitud desde la lista (puedes filtrar por estado o buscar por
      cliente/mascota). Verás el motivo, la mascota relacionada y los datos del
      cliente. Tienes tres opciones:
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Aprobar y compartir agenda</h3>
          <p class="text-gray-600">
            Si la solicitud aplica, pega el <strong>link de la agenda de Google
            Calendar</strong> (la página de reservas con los horarios que
            liberaste) y, si quieres, un mensaje. El cliente recibirá el botón
            "Reservar en la agenda" para elegir su horario.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Pedir más detalles</h3>
          <p class="text-gray-600">
            Si el motivo no es claro, escribe tu pregunta. La solicitud pasa a
            <strong>"Esperando cliente"</strong>; cuando el cliente responda
            desde su portal, regresa a <strong>"Pendiente"</strong> con su
            respuesta incluida.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Rechazar con justificación</h3>
          <p class="text-gray-600">
            Si no aplica, escribe la justificación para el cliente — por ejemplo,
            canalizando su atención al área correspondiente. El cliente puede
            crear una nueva solicitud después si lo necesita.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> usa las "Agendas de citas" de Google Calendar para
        generar la página de reservas: así el cliente solo ve los horarios que
        el especialista liberó, y la confirmación llega al calendario de ambos.
      </p>
    </div>

    <!-- Scheduling -->
    <h2>Registrar la cita</h2>
    <p>
      Cuando el cliente reserva un horario en la agenda de Google Calendar que
      compartiste, regresa a la solicitud (ahora en estado
      <strong>"Aprobada"</strong>) y usa <strong>"Registrar cita"</strong> para
      capturar la <strong>fecha y hora</strong> confirmadas y, si aplica, el
      <strong>link de la reunión (Meet)</strong>. Esto hace que la cita aparezca
      con todos sus detalles tanto en el panel de administración como en el
      portal del cliente. Si necesitas cambiar el horario después, usa
      <strong>"Reprogramar"</strong> desde la misma solicitud.
    </p>

    <!-- Closing -->
    <h2>Cerrar la cita</h2>
    <p>
      Después de que se lleve a cabo (o no) la reunión, cierra la solicitud con
      el resultado correspondiente:
    </p>

    <div class="info-box">
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Finalizada:</strong> la cita se atendió con normalidad.</li>
        <li><strong>No asistió:</strong> el cliente no se presentó a la cita.</li>
        <li>
          <strong>Cancelada:</strong> disponible tanto antes como después de
          registrar la cita, para cuando ya no se llevará a cabo. El cliente
          también puede cancelar su propia cita desde su portal.
        </li>
      </ul>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> el detalle de cada solicitud muestra el
        <strong>historial completo de mensajes</strong> intercambiados con el
        cliente (peticiones de información, respuestas, notas), no solo el
        último mensaje.
      </p>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Nota:</strong> una solicitud rechazada, o una cita ya cerrada
        (Finalizada, No asistió o Cancelada), no se puede volver a atender.
        Quién respondió y cuándo queda registrado en el detalle de cada
        solicitud.
      </p>
    </div>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualAppointmentRequests",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualClients", label: "Clientes" },
        { route: "manualQuoteRequests", label: "Solicitudes" },
        { route: "manualPets", label: "Mascotas" },
      ],
    };
  },
};
</script>
